import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useFilterOptions } from '../../hooks';
import type { FilterFieldConfig, FilterOption } from '../../types';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface SearchFilterProps {
  /** Field configuration */
  config: FilterFieldConfig<string>;
  /** Current value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Hide the label */
  hideLabel?: boolean;
}

// ============================================================================
// Utils
// ============================================================================

function getInitials(label: string): string {
  const parts = label.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  return label.substring(0, 2).toUpperCase();
}

// ============================================================================
// Component
// ============================================================================

/**
 * Searchable combobox filter field.
 * Supports filtering options by search query and shows avatars for user options.
 *
 * @example
 * ```tsx
 * <SearchFilter
 *   config={{
 *     key: 'managerId',
 *     type: 'search',
 *     label: 'Manager',
 *     placeholder: 'All managers',
 *     defaultValue: 'all',
 *     options: 'managers',
 *   }}
 *   value={filters.managerId}
 *   onChange={(value) => setFilter('managerId', value)}
 * />
 * ```
 */
export function SearchFilter({
  config,
  value,
  onChange,
  disabled = false,
  className,
  hideLabel = false,
}: SearchFilterProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { options, isLoading } = useFilterOptions(config.options);

  // Determine placeholder text
  const placeholder = config.placeholder ?? `All ${config.label.toLowerCase()}`;

  // Find selected option
  const selectedOption = useMemo(() => {
    if (value === 'all' || !value) return null;
    return options.find((opt) => opt.value === value);
  }, [value, options]);

  // Filter options by search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.description?.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  // Handle selection
  const handleSelect = (selectedValue: string) => {
    if (selectedValue === value) {
      // Deselect if clicking the same option
      onChange('all');
    } else {
      onChange(selectedValue);
    }
    setSearchQuery('');
    setOpen(false);
  };

  // Handle clear/reset to all
  const handleClear = () => {
    onChange('all');
    setSearchQuery('');
    setOpen(false);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {!hideLabel && (
        <label className="text-sm font-medium text-foreground">
          {config.label}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled}
          >
            {selectedOption ? (
              <SelectedOptionDisplay option={selectedOption} />
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${config.label.toLowerCase()}...`}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* "All" option to clear */}
                  <CommandGroup>
                    <CommandItem
                      value="__all__"
                      onSelect={handleClear}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === 'all' ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="text-muted-foreground">{placeholder}</span>
                    </CommandItem>
                  </CommandGroup>

                  {/* Filtered options */}
                  {filteredOptions.length === 0 ? (
                    <CommandEmpty>
                      {searchQuery ? 'No results found.' : 'No options available.'}
                    </CommandEmpty>
                  ) : (
                    <CommandGroup>
                      {filteredOptions.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          onSelect={() => handleSelect(option.value)}
                          disabled={option.disabled}
                          className="cursor-pointer"
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4 flex-shrink-0',
                              value === option.value ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          {config.renderOption ? (
                            config.renderOption(option)
                          ) : (
                            <SearchOptionContent option={option} />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ============================================================================
// Helper Components
// ============================================================================

interface OptionDisplayProps {
  option: FilterOption;
}

/**
 * Display for selected option in the trigger button.
 */
function SelectedOptionDisplay({ option }: OptionDisplayProps) {
  const hasAvatar = option.avatar !== undefined;

  if (hasAvatar) {
    return (
      <div className="flex items-center gap-2 min-w-0">
        <Avatar className="h-5 w-5 flex-shrink-0">
          <AvatarImage src={option.avatar} alt={option.label} />
          <AvatarFallback className="text-[10px]">
            {getInitials(option.label)}
          </AvatarFallback>
        </Avatar>
        <span className="truncate">{option.label}</span>
      </div>
    );
  }

  return <span className="truncate">{option.label}</span>;
}

/**
 * Content for option in the dropdown list.
 */
function SearchOptionContent({ option }: OptionDisplayProps) {
  const hasAvatar = option.avatar !== undefined;

  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {hasAvatar && (
        <Avatar className="h-6 w-6 flex-shrink-0">
          <AvatarImage src={option.avatar} alt={option.label} />
          <AvatarFallback className="text-xs">
            {getInitials(option.label)}
          </AvatarFallback>
        </Avatar>
      )}
      {option.icon && !hasAvatar && (
        <span className="flex-shrink-0">{option.icon}</span>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{option.label}</div>
        {option.description && (
          <div className="text-xs text-muted-foreground truncate">
            {option.description}
          </div>
        )}
      </div>
      {option.count !== undefined && (
        <span className="text-xs text-muted-foreground flex-shrink-0">
          ({option.count})
        </span>
      )}
    </div>
  );
}
